import { StoreKit } from '@nx/state';
import type { RootState as FrameworkRootState } from '@nx/state';
import { concat, difference, groupBy, isEmpty, sortBy, uniq } from 'lodash-es';

import { METADATA_SCAN_SAMPLE } from '../../services/metagraph';
import { stateIndexesMapper } from '../../services/queries/resultMapper';
import type { PathSegment } from '../../types/perspective';
import type { AddingIndex } from './perspectiveMetadata.types';

// the main idea is that the db should be considered large when it takes more than 10s or when the labels/Reltypes count is more than 500 for a full scan metadata
// This threshold was defined comparing different DBs
// More info: https://docs.google.com/document/d/1pUehZNXxs23PVyhf4t-NPRzY3qkkBNfe7wJT1Pffc3I/edit?tab=t.0#heading=h.etrg5q9qydy7
export const isLargeDb = (
  elementCount: number,
  maxLimitForLabelsAndRelTypes: number,
  lablesCount?: number,
  relTypesCount?: number,
) => {
  return (
    elementCount >= 10e6 ||
    (lablesCount ?? 0) >= maxLimitForLabelsAndRelTypes ||
    (relTypesCount ?? 0) >= maxLimitForLabelsAndRelTypes
  );
};

export const isLargeDbCheckForDefaultPerspective = (
  labelsCount: number,
  relTypesCount: number,
  maxLimitForLabelsAndRelTypes: number,
): boolean => {
  return labelsCount >= maxLimitForLabelsAndRelTypes || relTypesCount >= maxLimitForLabelsAndRelTypes;
};

export const getMetadataSampleScanMode = async () => Promise.resolve(METADATA_SCAN_SAMPLE);
export const removeBloomPrefixedValueFromGivenArray = (records: string[]) =>
  records.filter((record: string) => !record.startsWith('_Bloom_'));

export const getIndexedAndNonIndexedLabels = (labels: string[], indexes: AddingIndex[], limit: number) => {
  const allIndexedLabels = uniq(indexes.map((index) => index.label).filter((label) => !isEmpty(label)));
  const indexedLabels = allIndexedLabels.length > 0 ? sortBy(allIndexedLabels).slice(0, limit) : [];
  if (indexedLabels.length < limit) {
    const remainingLabels = limit - indexedLabels.length;
    const nonIndexedLabels = difference(labels, allIndexedLabels).slice(0, remainingLabels);
    return sortBy(concat(indexedLabels, nonIndexedLabels));
  }
  return sortBy(indexedLabels);
};

export const getRelevantRelTypes = (pathSegments: PathSegment[], limit: number, filteredLabels?: string[]) => {
  const filterPathSegmentsOnLabels = pathSegments.filter(
    (segment: PathSegment) => filteredLabels?.includes(segment.source) && filteredLabels?.includes(segment.target),
  );
  const groupedPathSegments = groupBy(filterPathSegmentsOnLabels, (segment: PathSegment) => segment.relationshipType);
  const sortedRelationshipTypesCount = sortBy(Object.entries(groupedPathSegments).map(([key]) => key));

  return sortedRelationshipTypesCount.slice(0, limit);
};
const getGlobalStateDataSummary = (state: FrameworkRootState) => state.dataSummary;

export const getIndexesFromGlobalState = () => {
  const { globalStore } = StoreKit.UNSAFE_getGlobalStoreInstance();
  const { data } = getGlobalStateDataSummary(globalStore.getState());
  return stateIndexesMapper(data?.indexes ?? []);
};
